import { Application, Container, Sprite, type FederatedPointerEvent, type Ticker as PixiTicker } from "pixi.js";
import type { TiledMap } from "pixi-tiledmap";
import type { AssetLoader } from "../assets/AssetLoader";
import { EntityView } from "./EntityView";
import { CharacterView } from "./CharacterView";
import { Camera } from "./Camera";
import { getComponent, type Entity, type EntityId } from "../world/Entity";
import type { Scene } from "../world/Scene";
import type { Vec2 } from "../world/geometry";
import type { World } from "../world/World";
import type { Ticker } from "../animation/tween";
import type { EntityDefinitionRegistry } from "../game-content/EntityDefinitionRegistry";
import { RadialMenu, type RadialAction } from "../ui/RadialMenu";
import type { ParsedTerrainGrid, ParsedTerrainObject } from "../world/terrain";
import { loadTerrainFromTiledFile } from "./TerrainRenderer";

export interface PixiRendererCallbacks {
  onEntityPointerDown?(entityId: EntityId, worldPosition: Vec2, pointerId: number): void;
  onStagePointerMove?(worldPosition: Vec2, pointerId: number): void;
  onStagePointerUp?(worldPosition: Vec2, pointerId: number): void;
}

type AnyEntityView = EntityView | CharacterView;

function containerOf(view: AnyEntityView): Container {
  return view instanceof CharacterView ? view.container : view.sprite;
}

/** Reads `World`/`Scene` state and syncs Pixi display objects. Never mutates game state. */
export class PixiRenderer {
  readonly app = new Application();
  readonly camera: Camera;
  readonly ticker: Ticker;

  private readonly worldContainer = new Container({ isRenderGroup: true });
  private readonly radialMenu: RadialMenu;
  private backgroundSprite?: Sprite;
  private views = new Map<EntityId, AnyEntityView>();
  private terrainMap: TiledMap | undefined;
  private _terrainGrid: ParsedTerrainGrid | undefined;
  private _terrainObjects: readonly ParsedTerrainObject[] = [];

  get terrainGrid(): ParsedTerrainGrid | undefined {
    return this._terrainGrid;
  }

  get terrainObjects(): readonly ParsedTerrainObject[] {
    return this._terrainObjects;
  }

  private constructor(
    private readonly assets: AssetLoader,
    private readonly entityDefinitions: EntityDefinitionRegistry,
    private readonly callbacks: PixiRendererCallbacks,
  ) {
    this.worldContainer.sortableChildren = true;
    this.camera = new Camera(this.worldContainer, { width: 0, height: 0 });
    this.ticker = {
      add: (fn) => {
        const listener = (t: PixiTicker) => fn(t.deltaMS);
        this.app.ticker.add(listener);
        return () => this.app.ticker.remove(listener);
      },
    };
    this.radialMenu = new RadialMenu(this.ticker);
  }

  static async create(
    container: HTMLElement,
    assets: AssetLoader,
    entityDefinitions: EntityDefinitionRegistry,
    callbacks: PixiRendererCallbacks = {},
  ): Promise<PixiRenderer> {
    const renderer = new PixiRenderer(assets, entityDefinitions, callbacks);
    await renderer.app.init({ resizeTo: container, backgroundColor: 0x1b1626, antialias: true });
    container.appendChild(renderer.app.canvas);

    renderer.app.stage.addChild(renderer.worldContainer);
    renderer.worldContainer.addChild(renderer.radialMenu.container);
    renderer.app.stage.eventMode = "static";
    renderer.app.stage.hitArea = renderer.app.screen;
    renderer.app.stage.on("pointermove", (e: FederatedPointerEvent) => {
      callbacks.onStagePointerMove?.(renderer.camera.screenToWorld({ x: e.global.x, y: e.global.y }), e.pointerId);
    });
    const emitUp = (e: FederatedPointerEvent) =>
      callbacks.onStagePointerUp?.(renderer.camera.screenToWorld({ x: e.global.x, y: e.global.y }), e.pointerId);
    renderer.app.stage.on("pointerup", emitUp);
    renderer.app.stage.on("pointerupoutside", emitUp);

    renderer.camera.resize({ width: renderer.app.screen.width, height: renderer.app.screen.height });
    renderer.app.renderer.on("resize", () =>
      renderer.camera.resize({ width: renderer.app.screen.width, height: renderer.app.screen.height }),
    );
    return renderer;
  }

  async loadTerrain(terrainFile: string): Promise<void> {
    this.terrainMap?.destroy();
    this.terrainMap = undefined;
    this._terrainGrid = undefined;
    this._terrainObjects = [];
    const terrain = await loadTerrainFromTiledFile(terrainFile, this.assets);
    this._terrainGrid = terrain.grid;
    this._terrainObjects = terrain.objects;
    this.terrainMap = terrain.map;
    this.terrainMap.zIndex = -0.5;
    this.terrainMap.cullable = true;
    this.worldContainer.addChild(this.terrainMap);
  }

  async loadScene(scene: Scene): Promise<void> {
    await this.assets.loadKeys(collectAssetKeys(scene));

    for (const view of this.views.values()) view.destroy();
    this.views.clear();
    this.backgroundSprite?.destroy();

    const background = this.assets.textureFor(scene.definition.background);
    this.backgroundSprite = new Sprite(background.texture);
    this.backgroundSprite.zIndex = -1;
    this.backgroundSprite.cullable = true;
    this.worldContainer.addChildAt(this.backgroundSprite, 0);

    if (!this.terrainMap) {
      await this.loadTerrain(scene.definition.terrainFile);
    }

    this.camera.setBounds({ x: 0, y: 0, width: scene.definition.size.width, height: scene.definition.size.height });

    for (const entity of scene.entities) this.createView(entity);
  }

  syncEntities(entities: readonly Entity[]): void {
    const seen = new Set<EntityId>();
    for (const entity of entities) {
      seen.add(entity.id);
      const view = this.views.get(entity.id);
      if (view) view.sync(entity);
      else this.createView(entity);
    }
    for (const [id, view] of this.views) {
      if (!seen.has(id)) {
        view.destroy();
        this.views.delete(id);
      }
    }
  }

  update(deltaSeconds: number, world: World): void {
    this.camera.update(deltaSeconds, world);
  }

  getViewContainer(entityId: EntityId): Container | undefined {
    const view = this.views.get(entityId);
    return view && containerOf(view);
  }

  showActionsMenu(
    worldPos: Vec2,
    entityName: string,
    actions: readonly RadialAction[],
    onPick: (id: string) => void,
  ): void {
    this.radialMenu.show(worldPos, entityName, actions, onPick, this.app.stage);
  }

  hideActionsMenu(): void {
    this.radialMenu.hide();
  }

  get isRadialMenuOpen(): boolean {
    return this.radialMenu.isOpen;
  }

  private createView(entity: Entity): void {
    const renderable = getComponent(entity, "renderable");
    const character = getComponent(entity, "character");
    const assetKey = renderable?.asset ?? (character ? undefined : entity.definitionId);
    if (assetKey) this.assets.loadKeys([assetKey]);

    const isCharacter = character !== undefined;
    let view: AnyEntityView;
    if (isCharacter) {
      view = new CharacterView(entity, this.assets, this.entityDefinitions);
    } else {
      view = new EntityView(entity, this.assets);
    }
    this.views.set(entity.id, view);
    const displayObject = containerOf(view);
    displayObject.cullable = true;
    this.worldContainer.addChild(displayObject);

    if (getComponent(entity, "draggable") || getComponent(entity, "clickable")) {
      displayObject.eventMode = "static";
      displayObject.cursor = "pointer";
      displayObject.on("pointerdown", (e: FederatedPointerEvent) => {
        this.callbacks.onEntityPointerDown?.(entity.id, this.camera.screenToWorld({ x: e.global.x, y: e.global.y }), e.pointerId);
        e.stopPropagation();
      });
    }
  }
}

function collectAssetKeys(scene: Scene): string[] {
  const keys = new Set<string>([scene.definition.background]);
  for (const entity of scene.entities) {
    const renderable = getComponent(entity, "renderable");
    if (renderable) keys.add(renderable.asset);
    const character = getComponent(entity, "character");
    if (character) {
      for (const layer of character.layers) keys.add(layer);
      for (const value of Object.values(character.clothing)) if (value) keys.add(value);
    }
  }
  return [...keys];
}
